import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const rules = [
  { key: "len", label: "Au moins 8 caractères", test: (p) => p.length >= 8 },
  { key: "maj", label: "Au moins une majuscule", test: (p) => /[A-Z]/.test(p) },
  { key: "sym", label: "Au moins un symbole (! ? @ # $ …)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export const PasswordRules = ({ value }) => (
  <ul className="space-y-1 mb-5" data-testid="password-rules">
    {rules.map((r) => {
      const ok = r.test(value || "");
      return (
        <li key={r.key} className={`text-xs flex items-center gap-1.5 ${ok ? "text-bleu" : "text-marine/80"}`}>
          {ok ? <Check size={16} /> : <X size={16} className="text-muet" />} {r.label}
        </li>
      );
    })}
  </ul>
);

export const isValidPassword = (p) => rules.every((r) => r.test(p || ""));

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidPassword(pwd)) { toast.error("Le mot de passe ne respecte pas les critères de sécurité."); return; }
    if (pwd !== confirm) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Mot de passe réinitialisé. Vous pouvez vous connecter.");
      nav("/login");
    } catch (err) { toast.error(err.response?.data?.detail || "Lien invalide ou expiré."); }
    finally { setBusy(false); }
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center p-8 beige-texture">
      <div className="w-full max-w-sm bg-sable rounded-2xl border border-marine/10 shadow-md p-10 text-center">
        <h1 className="font-display text-2xl text-marine mb-2">Lien invalide</h1>
        <p className="text-sm text-marine/80 mb-6">Ce lien de réinitialisation est incomplet ou a expiré.</p>
        <Link to="/mot-de-passe-oublie" className="text-sm text-bordeaux hover:underline">Refaire une demande</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-8 beige-texture">
      <form onSubmit={submit} className="w-full max-w-sm bg-sable rounded-2xl border border-marine/10 shadow-md p-10" data-testid="reset-card">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-bordeaux mb-2">Nouveau mot de passe</div>
        <h1 className="font-display text-3xl text-marine mb-5">Choisir un mot de passe</h1>
        <label className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Nouveau mot de passe</label>
        <input data-testid="reset-password" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="inp mb-3" />
        <PasswordRules value={pwd} />
        <label className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Confirmer</label>
        <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="inp mb-6" />
        <button data-testid="reset-submit" disabled={busy} className="w-full bg-bordeaux text-white rounded-xl py-3 text-sm font-semibold hover:bg-bordeaux-hover transition-colors active:scale-95 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Réinitialiser
        </button>
      </form>
    </div>
  );
}
